import { regular, solid } from '@fortawesome/fontawesome-svg-core/import.macro'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Card, Col, ColorPicker, Form, Row, Segmented, Space } from 'antd'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Breadcrumb, Button, Container } from 'react-bootstrap'
import { Link } from 'react-router-dom'
import IconButton from '../../components/common/IconButton'
import ScriptEditor from '../../components/common/ScriptEditor'
import { ControlPanelManager } from '../../api/ControlPanelManager';
import colorVariables, { defaultFileContent } from '../../utils/uiVariables';
import { useToasts } from '../../contexts/toasts'
import { useFullScreenLoader } from '../../contexts/fullScreenLoader'
import chroma from 'chroma-js'
import { EventEmitter } from '../../utils/EventEmitter'
import { Color } from 'antd/es/color-picker'

const editModeOptions = [
  {
    label: (
      <>
        <FontAwesomeIcon icon={solid('hashtag')} />
        <span className="ms-2">Variables</span>
      </>
    ),
    value: 'variables'
  },
  {
    label: (
      <>
        <FontAwesomeIcon icon={regular('file-code')} />
        <span className="ms-2">File</span>
      </>
    ),
    value: 'file'
  },
]

export default function UICustomization() {
  const [colorsForm] = Form.useForm();
  const [editMode, setEditMode] = useState('variables');
  const [fileContent, setFileContent] = useState<string>();
  const [timestamp, setTimestamp] = useState(new Date().getTime());
  const mainLogoRef = useRef<HTMLInputElement>(null);
  const secondaryLogoRef = useRef<HTMLInputElement>(null);
  const sidebarLogoRef = useRef<HTMLInputElement>(null);
  const notFoundRef = useRef<HTMLInputElement>(null);

  const { showToast } = useToasts();
  const { showLoader, hideLoader } = useFullScreenLoader();

  const resetFileContent = useCallback(() => setFileContent(defaultFileContent), []);
  const resetColors = useCallback(() => colorsForm.setFieldsValue(colorVariables), [colorsForm]);
  const updateTimestamp = useCallback(() => {
    const ts = new Date().getTime();
    EventEmitter.dispatch('update-timestamp', ts);
    setTimestamp(ts);
  }, []);

  const initFileContent = useCallback(() => {
    ControlPanelManager.getStylesFile()
      .then(
        ({ data }) => setFileContent(data || defaultFileContent),
        (_err) => showToast('file-error', null, 'Could not download remote file, falling back to default values', 'warning'),
      );
  }, [showToast]);

  useEffect(() => {
    resetColors();
    initFileContent();
  }, [resetColors, initFileContent]);

  const uploadImage = useCallback((type: 'brand' | 'secondary' | 'sidebar' | '404') => () => {
    if (type === 'brand') mainLogoRef.current!.click();
    else if (type === 'secondary') secondaryLogoRef.current!.click();
    else if (type === 'sidebar') sidebarLogoRef.current!.click();
    else notFoundRef.current!.click();
  }, []);

  const uploadFile = useCallback(() => {
    const formData = new FormData();
    const file = new Blob([fileContent!], { type: 'text/css' });
    formData.set('file', file);

    showLoader();

    ControlPanelManager.uploadStylesFile(formData)
      .then(
        () => {
          hideLoader();
          updateTimestamp();
        },
        (err) => {
          const message = err.response?.data?.message || err.message;
          showToast('file-upload-error', message, 'Save Error', 'error');
          hideLoader();
        }
      )
  }, [fileContent, showLoader, hideLoader, showToast, updateTimestamp]);

  const handleInputChange = useCallback((type: 'brand' | 'secondary' | 'sidebar' | '404') => (evt: React.ChangeEvent<HTMLInputElement>) => {
    const file = evt.target.files![0];
    if (!file) return;

    const formData = new FormData();
    formData.set('file', file);
    formData.set('image', type);

    showLoader();

    ControlPanelManager.updateImage(formData)
      .then(
        () => {
          hideLoader();
          updateTimestamp();
        },
        (err) => {
          const message = err.response?.data?.message || err.message;
          showToast('file-upload-error', message, 'Save Error', 'danger');
          hideLoader();
        }
      )
  }, [showLoader, hideLoader, showToast, updateTimestamp]);

  const renderColorPickerLabel = useCallback((label: string) => (color: Color) => (
    <span>{label}: <b style={{ color: color.toHexString() }}>{color.toHexString()}</b></span>
  ), []);

  const submitColors = useCallback((values: any) => {
    const body = {
      ...values,
      brandPrimaryRgb: chroma(typeof values.brandPrimary === 'string' ? values.brandPrimary : values.brandPrimary.toHexString()).rgb().join(","),
      brandSecondaryRgb: chroma(typeof values.brandSecondary === 'string' ? values.brandSecondary : values.brandSecondary.toHexString()).rgb().join(","),
      datasourcePrimaryRgb: chroma(typeof values.datasourcePrimary === 'string' ? values.datasourcePrimary : values.datasourcePrimary.toHexString()).rgb().join(","),
      datasetPrimaryRgb: chroma(typeof values.datasetPrimary === 'string' ? values.datasetPrimary : values.datasetPrimary.toHexString()).rgb().join(","),
      modelPrimaryRgb: chroma(typeof values.modelPrimary === 'string' ? values.modelPrimary : values.modelPrimary.toHexString()).rgb().join(","),
    }
    showLoader();

    ControlPanelManager.updateStyleVariables(body)
      .then(
        () => {
          hideLoader();
          updateTimestamp();
        },
        (err) => {
          const message = err.response?.data?.message || err.message;
          showToast('variables-update-error', message, 'Variables Error', 'error');
          hideLoader();
        }
      )
  }, [showLoader, hideLoader, updateTimestamp, showToast]);

  const renderScriptEditor = useCallback(() => {
    return (
      <>
        <ScriptEditor language="css" value={fileContent} onChange={setFileContent} height="55vh" />
        <Row className="mt-3" justify="space-between">
          <Button size="sm" variant="light" className="border" onClick={resetFileContent}>
            <FontAwesomeIcon icon={solid('undo')} />
            <span className="ms-2">Reset Content</span>
          </Button>
          <Button variant="success" className="px-3" size="sm" onClick={uploadFile}>
            <FontAwesomeIcon icon={solid('file-arrow-up')} />
            <span className="ms-2">Confirm</span>
          </Button>
        </Row>
      </>
    );
  }, [resetFileContent, fileContent, uploadFile]);

  const renderForm = useCallback(() => {
    return (
      <Form form={colorsForm} onFinish={submitColors}>
        <h6 className="mb-1" style={{ color: colorVariables.brandPrimary }}>Brand</h6>
        <Space size={10} className="w-100 mb-3">
          <Form.Item noStyle name="brandPrimary">
            <ColorPicker showText={renderColorPickerLabel('Primary')} />
          </Form.Item>
          <Form.Item noStyle name="brandSecondary">
            <ColorPicker showText={renderColorPickerLabel('Secondary')} />
          </Form.Item>
          <Form.Item noStyle name="brandDark">
            <ColorPicker showText={renderColorPickerLabel('Dark')} />
          </Form.Item>
        </Space>

        <h6 className="mb-1" style={{ color: colorVariables.datasourcePrimary }}>Data Sources</h6>
        <Space size={10} className="w-100 mb-3">
          <Form.Item noStyle name="datasourcePrimary">
            <ColorPicker showText={renderColorPickerLabel('Primary')} />
          </Form.Item>
          <Form.Item noStyle name="datasourceDark">
            <ColorPicker showText={renderColorPickerLabel('Dark')} />
          </Form.Item>
        </Space>

        <h6 className="mb-1" style={{ color: colorVariables.datasetPrimary }}>Datasets</h6>
        <Space size={10} className="w-100 mb-3">
          <Form.Item noStyle name="datasetPrimary">
            <ColorPicker showText={renderColorPickerLabel('Primary')} />
          </Form.Item>
          <Form.Item noStyle name="datasetDark">
            <ColorPicker showText={renderColorPickerLabel('Dark')} />
          </Form.Item>
        </Space>

        <h6 className="mb-1" style={{ color: colorVariables.modelPrimary }}>Models</h6>
        <Space size={10} className="w-100 mb-3">
          <Form.Item noStyle name="modelPrimary">
            <ColorPicker showText={renderColorPickerLabel('Primary')} />
          </Form.Item>
          <Form.Item noStyle name="modelDark">
            <ColorPicker showText={renderColorPickerLabel('Dark')} />
          </Form.Item>
        </Space>

        <h6 className="mb-1" style={{ color: colorVariables.processorPrimary }}>Services</h6>
        <Space size={10} className="w-100 mb-1" wrap>
          <Form.Item noStyle name="sourcePrimary">
            <ColorPicker showText={renderColorPickerLabel('Source')} />
          </Form.Item>
          <Form.Item noStyle name="sourceDark">
            <ColorPicker showText={renderColorPickerLabel('Source Dark')} />
          </Form.Item>
        </Space>
        <Space size={10} className="w-100 mb-1" wrap>
          <Form.Item noStyle name="processorPrimary">
            <ColorPicker showText={renderColorPickerLabel('Processor')} />
          </Form.Item>
          <Form.Item noStyle name="processorDark">
            <ColorPicker showText={renderColorPickerLabel('Processor Dark')} />
          </Form.Item>
        </Space>
        <Space size={10} className="w-100 mb-1" wrap>
          <Form.Item noStyle name="sinkPrimary">
            <ColorPicker showText={renderColorPickerLabel('Sink')} />
          </Form.Item>
          <Form.Item noStyle name="sinkDark">
            <ColorPicker showText={renderColorPickerLabel('Sink Dark')} />
          </Form.Item>
        </Space>
        <Space size={10} className="w-100" wrap>
          <Form.Item noStyle name="batchPrimary">
            <ColorPicker showText={renderColorPickerLabel('Batch')} />
          </Form.Item>
          <Form.Item noStyle name="batchDark">
            <ColorPicker showText={renderColorPickerLabel('Batch Dark')} />
          </Form.Item>
          <Form.Item noStyle name="streamPrimary">
            <ColorPicker showText={renderColorPickerLabel('Stream')} />
          </Form.Item>
        </Space>

        <Row justify="space-between" className="mt-4">
          <Button variant="light" className="border" size="sm" type="button" onClick={resetColors}>
            <FontAwesomeIcon icon={solid('arrow-rotate-left')} />
            <span className="ms-2">Revert</span>
          </Button>
          <Button type="submit" variant="success" size="sm" className="px-3">
            <FontAwesomeIcon icon={solid('save')} />
            <span className="ms-2">Save</span>
          </Button>
        </Row>
      </Form>
    )
  }, [colorsForm, submitColors, resetColors, renderColorPickerLabel]);

  return (
    <Container className="my-3">
      <Breadcrumb>
        <Breadcrumb.Item linkAs={Link} linkProps={{ to: "/control-panel" }}>Control Panel</Breadcrumb.Item>
        <Breadcrumb.Item active>User Interface Customization</Breadcrumb.Item>
      </Breadcrumb>

      <Card>
        <Row align="middle" gutter={20} className="mb-4">
          <Col>
            <FontAwesomeIcon icon={solid('sliders-h')} size="2x" />
          </Col>

          <Col flex="1 0">
            <h6 className="mb-1">User Interface Customization</h6>
            <span className="text-muted">Personalize the look of this instance of Alida according to your taste.</span>
          </Col>
        </Row>

        <Row gutter={30}>
          <Col md={12}>
            <Row align="middle" className="mb-2">
              <Col flex="1 0">
                <FontAwesomeIcon icon={solid('palette')} />
                <b className="ms-2">Customize Colors</b>
              </Col>
              <Segmented value={editMode} onChange={setEditMode} options={editModeOptions} />
            </Row>

            {editMode === 'variables' ? renderForm() : renderScriptEditor()}
          </Col>

          <Col md={12} className="border-start">
            <div className="d-flex align-items-center mb-2">
              <FontAwesomeIcon icon={solid('image')} />
              <b className="ms-2">Change Logos and Images</b>
            </div>

            <Row align="middle" className="border rounded mb-3">
              <Col span={8} className="text-center p-3">
                <img id="main-logo-preview" className="logo-preview" src={`${window.__env.LOGO_URL}?t=${timestamp}`} alt="Alida brand" />
              </Col>
              <Col flex="1 0" className="p-2">
                <h6 className="mb-1">Brand picture</h6>
                <span className="text-muted">Main logo used in the login screen or the navbar.</span>
              </Col>
              <IconButton className="me-3" onClick={uploadImage('brand')} tooltip="Choose Picture">
                <FontAwesomeIcon icon={solid('pen')} />
              </IconButton>
              <input onChange={handleInputChange('brand')} multiple={false} ref={mainLogoRef} type="file" hidden accept="image/*" />
            </Row>

            <Row align="middle" className="border rounded mb-3">
              <Col span={8} className="text-center p-3">
                <img id="secondary-logo-preview" className="logo-preview" src={`${window.__env.GREY_LOGO_URL}?t=${timestamp}`} alt="Alida secondary" />
              </Col>
              <Col flex="1 0" className="p-2">
                <h6 className="mb-1">Secondary picture</h6>
                <span className="text-muted">Alternative version used in the dashboard screen.</span>
              </Col>
              <IconButton className="me-3" onClick={uploadImage('secondary')} tooltip="Choose Picture">
                <FontAwesomeIcon icon={solid('pen')} />
              </IconButton>
              <input onChange={handleInputChange('secondary')} multiple={false} ref={secondaryLogoRef} type="file" hidden accept="image/*" />
            </Row>

            <Row align="middle" className="border mb-3">
              <Col span={8} className="text-center p-3">
                <img id="sidebar-logo-preview" className="logo-preview" src={`${window.__env.SIDEBAR_BG_IMAGE}?t=${timestamp}`} alt="Alida sidebar" />
              </Col>
              <Col flex="1 0" className="p-2">
                <h6 className="mb-1">Sidebar picture</h6>
                <span className="text-muted">Picture used as background in the sidebar.</span>
              </Col>
              <IconButton className="me-3" onClick={uploadImage('sidebar')} tooltip="Choose Picture">
                <FontAwesomeIcon icon={solid('pen')} />
              </IconButton>
              <input onChange={handleInputChange('sidebar')} multiple={false} ref={sidebarLogoRef} type="file" hidden accept="image/*" />
            </Row>

            <Row align="middle" className="border">
              <Col span={8} className="text-center p-3">
                <img id="not-found-preview" className="logo-preview" src={`${window.__env.NOT_FOUND_IMAGE}?t=${timestamp}`} alt="Alida Not Found" />
              </Col>
              <Col flex="1 0" className="p-2">
                <h6 className="mb-1">Not Found picture</h6>
                <span className="text-muted">Picture used in the 404 page.</span>
              </Col>
              <IconButton className="me-3" onClick={uploadImage('404')} tooltip="Choose Picture">
                <FontAwesomeIcon icon={solid('pen')} />
              </IconButton>
              <input onChange={handleInputChange('404')} multiple={false} ref={notFoundRef} type="file" hidden accept="image/*" />
            </Row>
          </Col>
        </Row>
      </Card>
    </Container>
  )
}
